/**
 * Sales assistant — entry point.
 *
 * Defines <sales-assistant> and does as close to nothing as possible. The reasoning
 * engine, the catalog client and the view are all pulled in by a dynamic import that
 * fires only once the shopper shows intent.
 *
 * Performance contract for this file: no static import of anything heavy, no network, no
 * layout work. The launcher is position:fixed with an explicit size, so it sits outside
 * layout flow and cannot contribute to CLS or become the LCP element.
 */

const PREFETCH_EVENTS = ['pointerenter', 'focusin', 'touchstart'];
const IDLE_DELAY_MS = 3000;

class SalesAssistant extends HTMLElement {
  /** @type {Promise<any>|null} */
  #loading = null;
  /** @type {any|null} */
  #controller = null;
  #teardown = [];

  connectedCallback() {
    // `limit` in the schema is per section group, so a second instance is still possible
    // if the section is added elsewhere. Only the first one initialises.
    if (document.querySelectorAll('sales-assistant').length > 1 && this.#isNotFirst()) {
      this.hidden = true;
      return;
    }

    const launcher = this.querySelector('[data-sa-launcher]');
    if (!launcher) return;

    const onLauncherClick = (event) => {
      event.preventDefault();
      this.#activate(true);
    };
    launcher.addEventListener('click', onLauncherClick);
    this.#teardown.push(() => launcher.removeEventListener('click', onLauncherClick));

    // Warm the module graph on the first sign of interest, so the panel opens instantly.
    const prefetch = () => this.#load();
    for (const type of PREFETCH_EVENTS) {
      launcher.addEventListener(type, prefetch, { once: true, passive: true });
      this.#teardown.push(() => launcher.removeEventListener(type, prefetch));
    }

    this.#scheduleIdlePrefetch();
    this.#maybeRunSelfTest();
  }

  /**
   * Load the regression suite only when explicitly asked for via ?sa_selftest=1.
   * Shoppers never fetch this module.
   */
  #maybeRunSelfTest() {
    try {
      if (!new URLSearchParams(window.location.search).has('sa_selftest')) return;
    } catch {
      return;
    }

    import('@theme/sales-assistant-selftest')
      .then((module) => module.reportSelfTest())
      .catch((error) => {
        document.dispatchEvent(
          new CustomEvent('sales-assistant:error', {
            detail: { message: 'Self-test could not be loaded', error },
          })
        );
      });
  }

  disconnectedCallback() {
    for (const off of this.#teardown) off();
    this.#teardown = [];
    this.#controller?.dispose();
    this.#controller = null;
  }

  /** @returns {boolean} */
  #isNotFirst() {
    return document.querySelector('sales-assistant') !== this;
  }

  /**
   * Prefetch when the browser is idle, but never on a connection where that would be
   * rude: Save-Data and slow networks opt out entirely.
   */
  #scheduleIdlePrefetch() {
    const connection = /** @type {any} */ (navigator).connection;
    if (connection?.saveData === true) return;
    if (connection?.effectiveType && connection.effectiveType !== '4g') return;

    const idle = /** @type {any} */ (window).requestIdleCallback;
    if (typeof idle !== 'function') return;

    const schedule = () => idle(() => this.#load(), { timeout: IDLE_DELAY_MS });
    if (document.readyState === 'complete') {
      window.setTimeout(schedule, IDLE_DELAY_MS);
    } else {
      window.addEventListener('load', () => window.setTimeout(schedule, IDLE_DELAY_MS), { once: true });
    }
  }

  /**
   * @returns {Promise<any>} The controller module namespace.
   */
  #load() {
    if (!this.#loading) {
      this.#loading = import('@theme/sales-assistant-controller').catch((error) => {
        // Reset so a later click can retry rather than being stuck on a failed import.
        this.#loading = null;
        throw error;
      });
    }
    return this.#loading;
  }

  /**
   * @param {boolean} shouldToggle - Whether this was a real press, as opposed to a prefetch.
   */
  async #activate(shouldToggle) {
    try {
      const module = await this.#load();

      if (!this.#controller) {
        this.#controller = new module.AssistantController(this, {
          cacheVersion: this.dataset.cacheVersion || 'v0',
        });

        if (!this.#controller.isUsable) {
          this.#controller = null;
          return;
        }
      }
      // Toggle rather than open: this is the only listener on the launcher, so it has to
      // handle the press that closes the panel as well as the one that opens it.
      if (shouldToggle) this.#controller.toggle();
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent('sales-assistant:error', {
          detail: { message: 'The sales assistant failed to load', error },
        })
      );
      // Fall back to the plain phone link the markup already contains, so the shopper is
      // never left with a dead button.
      this.dataset.failed = 'true';
    }
  }
}

if (!customElements.get('sales-assistant')) {
  customElements.define('sales-assistant', SalesAssistant);
}
